/**
 * The three pieces of text an AI answer needs to be able to quote us
 * (P25009-409, BR-1 / BR-2 / BR-3):
 *
 *   packageFormulaTitle   the formula title / H1 for a package: what it is,
 *                         which car, which size, which bolt pattern.
 *   listingAnswerSentence the first paragraph — one sentence a machine can
 *                         lift whole and still be correct.
 *   fitFactRows           the fit facts, in a fixed reading order, ready to be
 *                         rendered as a real table.
 *
 * Why deterministic and not written by a model: an AI answer engine reads the
 * first line hardest, and it reads it as fact. A title that varies between two
 * packages for the same car — which is what a language model produces — makes
 * the two look like different products. The package publisher still asks a model
 * for the DESCRIPTION, where variety is worth having; the title, the answer
 * sentence and the table are generated from the listing's own fields so they
 * cannot drift, cannot hallucinate a fact the listing does not carry, and say
 * exactly the same thing every time.
 *
 * Every function here refuses to invent. A fact that is absent is left out of
 * the sentence rather than guessed at, because these strings are also mirrored
 * into JSON-LD, and a claim in structured data that the page cannot back is
 * cloaking.
 *
 * Written as CommonJS ON PURPOSE, like src/util/packageCandidateCore.js and
 * src/util/fitmentFactsCore.js: this module is shared by the browser bundle (the
 * listing page and the /hjulpaket answer pages) and by the plain-Node API server
 * (the package publisher, which writes the title into the listing). Keep it free
 * of any browser- or Node-only API.
 */

/* The plausible ranges a road car's fitment falls in. Shared with the import
 * checks rather than restated, so "is this number possible" has one answer. */
const { RANGES } = require('./fitmentFactsCore');
const { listingTarget } = require('./serviceTargetingCore');
const { boltCircleMm } = require('./boltCircleCore');

const clean = value => (value === null || value === undefined ? '' : String(value).trim());

const has = value => clean(value) !== '';

/* Swedish season words. The keys are the camelCase values the Console stores —
 * several older code paths use snake_case ones that never match anything, so
 * both spellings are accepted here rather than silently dropping the season. */
const SEASON = {
  summer: { adjective: 'sommar', qualifier: '', noun: 'sommardäck' },
  winterFriction: { adjective: 'vinter', qualifier: 'friktion', noun: 'vinterdäck' },
  winterDubbed: { adjective: 'vinter', qualifier: 'dubbat', noun: 'vinterdäck' },
  rdack: { adjective: '', qualifier: '', noun: 'R-däck' },
  cdack: { adjective: '', qualifier: '', noun: 'C-däck' },
};
const SEASON_ALIASES = {
  winter_friction: 'winterFriction',
  winterfriction: 'winterFriction',
  winter_dubbed: 'winterDubbed',
  winterdubbed: 'winterDubbed',
  r_dack: 'rdack',
  c_dack: 'cdack',
};

const seasonOf = tiretype => {
  const raw = clean(tiretype);
  if (!raw) return null;
  const key = SEASON[raw] ? raw : SEASON_ALIASES[raw.toLowerCase()];
  return key ? SEASON[key] : null;
};

/* What each kind of package is called as one word. Matches
 * PACKAGE_TYPE_NOUNS in packageCandidateCore.js — kept as its own map because
 * the title needs the season folded into the compound word ("vinterhjulpaket"),
 * which is a property of the sentence rather than of the package. */
const PACKAGE_NOUN = { wheel: 'hjulpaket', tire: 'däckpaket', rim: 'fälgpaket' };

/* The same three products sold one at a time rather than as a package. The
 * season still folds into the compound word, so a winter complete wheel is a
 * "vinterhjul" and not a "vinter hjul". Kept separate from PACKAGE_NOUN
 * because calling a single imported tyre a "däckpaket" is simply wrong, and
 * that is what reusing the package title would have produced.
 */
const LISTING_NOUN = { wheel: 'hjul', tire: 'däck', rim: 'fälg' };

/* Which of the three a listing is. A package says so in `packageType`; a plain
 * seller listing only has `adType`, and the two use the same three words. */
const kindOf = (facts = {}) => {
  const packageType = clean(facts.packageType);
  if (PACKAGE_NOUN[packageType]) return packageType;
  const adType = clean(facts.adType);
  return PACKAGE_NOUN[adType] ? adType : null;
};

/** "235/55R19" for one axle, or '' when the size is not fully known. */
const tireSize = (facts = {}, axle = 'Front') => {
  const width = clean(facts[`tireWidth${axle}`]);
  const profile = clean(facts[`profile${axle}`]);
  const diameter = clean(facts.diameter);
  return width && profile && diameter ? `${width}/${profile}R${diameter}` : '';
};

/**
 * The tyre size(s) as one phrase. A staggered car takes a different size on
 * each axle, and saying only the front one describes a set that will not be
 * shipped, so both are named and labelled.
 *
 * @param {Object} facts listing publicData, or a package candidate
 * @returns {String} "235/55R19", "225/40R19 fram / 255/35R19 bak", or ''
 */
const tireSizePhrase = (facts = {}) => {
  const front = tireSize(facts, 'Front');
  const rear = tireSize(facts, 'Rear');
  if (front && rear && front !== rear) {
    return `${front} fram / ${rear} bak`;
  }
  return front || rear;
};

/**
 * "5x108" — bolt count by bolt circle, the pair or nothing.
 *
 * The circle is read by boltCircleCore, so every stored spelling prints as the
 * number it means: Console's keys `114_3` and `1397` (which the wizard and,
 * since P25009-488, every import store) read "5x114.3" and "6x139.7". Before,
 * `1397` printed as "6x1397", a bolt circle of 1.4 metres.
 *
 * An implausible pair is still dropped rather than printed. In a title that an
 * answer engine reads as fact, a wrong number is worse than a missing one, so
 * the same plausibility ranges the import checks against are applied here.
 */
const boltPatternLabel = (facts = {}) => {
  // A candidate carries the pattern as one string; a listing carries the halves.
  const stored = clean(facts.boltPattern);
  if (stored) return stored.replace(/\s*x\s*/i, 'x');

  const bolts = clean(facts.numberOfBolts);
  const circle = boltCircleMm(facts.boltCircle);
  if (!bolts || circle === null) return '';

  const inRange = (value, [min, max]) => {
    const n = Number.parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(n) && n >= min && n <= max;
  };
  if (!inRange(bolts, RANGES.numberOfBolts) || !inRange(circle, RANGES.boltCircle)) {
    return '';
  }
  return `${bolts}x${circle}`;
};

/** "ET45", including a negative offset. '' when there is no offset on file. */
const etLabel = (facts = {}) => {
  const et = facts.etFront;
  return et === null || et === undefined || clean(et) === '' ? '' : `ET${clean(et)}`;
};

/** "Volvo XC60", preferring the display names a package candidate carries. */
const carLabel = (facts = {}) => {
  const make = clean(facts.displayMake) || clean(facts.make) || clean(facts.categoryLevel1);
  const model = clean(facts.displayModel) || clean(facts.model) || clean(facts.categoryLevel2);
  /* "blank" is what the bulk-upload path writes when the seller left the car
   * out. Read as absent: a title saying "passar Blank" is worse than one that
   * names no car at all. */
  const usable = value => (value && value.toLowerCase() !== 'blank' ? value : '');
  return [usable(make), usable(model)].filter(Boolean).join(' ');
};

/** "(2017–)" when the years say something, otherwise ''. */
const yearPhrase = (facts = {}) => {
  const from = clean(facts.yearFrom) || clean(facts.year);
  const to = clean(facts.yearTo);
  if (from && to && from !== to) return `(${from}–${to})`;
  if (from) return `(${from}–)`;
  return '';
};

/**
 * The formula title for a package listing (BR-1).
 *
 * Shape: <what it is> <car> – <size>, <bolt pattern> <ET>, <montering>
 *   "Komplett vinterhjulpaket Volvo XC60 – 235/55R19 dubbat, 5x108 ET45, monterat och balanserat"
 *
 * Only "monterat och balanserat" is a claim rather than a measurement, and it is
 * added for wheel packages alone — that is what a hjulpaket IS (a tyre already
 * fitted to a rim), so it is true by definition of the product. A tyre-only or
 * rim-only package never says it.
 *
 * @param {Object} facts package candidate or a package listing's publicData
 * @returns {String} the title, or '' when there is not enough to build one
 */
const packageFormulaTitle = (facts = {}, { nouns = PACKAGE_NOUN, mounting = true } = {}) => {
  const kind = kindOf(facts);
  if (!kind) return '';

  const season = seasonOf(facts.tiretype);
  // A rim has no season, so it never gets a season prefix even if one is stored.
  const seasonAdjective = kind === 'rim' ? '' : (season && season.adjective) || '';
  const noun = `${seasonAdjective}${nouns[kind]}`;
  // "Komplett" is only honest about a wheel package: rim plus tyre, ready to fit.
  const lead = kind === 'wheel' ? `Komplett ${noun}` : noun.charAt(0).toUpperCase() + noun.slice(1);

  const car = carLabel(facts);
  const diameterLabel = clean(facts.diameter) ? `${clean(facts.diameter)}"` : '';
  const size =
    kind === 'rim'
      ? [diameterLabel, boltPatternLabel(facts)].filter(Boolean).join(' ')
      : /* A tyre or wheel package states the full dimension when it has one, and
         * falls back to the rim diameter when it does not. Plenty of published
         * packages carry `diameter` but no tyre width — an Audi A6 winter set
         * read "Komplett vinterhjulpaket Audi A6 – dubbat, 5x112 ET42" with no
         * size at all, when the listing knew perfectly well it was 18". The
         * diameter is also the number a buyer asks for out loud. */
        tireSizePhrase(facts) || diameterLabel;

  // Without a car AND without a size there is nothing a formula can add over
  // whatever the seller wrote, so the caller keeps its own title.
  if (!car && !size) return '';

  const seasonQualifier = kind === 'rim' ? '' : (season && season.qualifier) || '';
  const specs = [
    [size, seasonQualifier].filter(Boolean).join(' '),
    // The bolt pattern is already in `size` for a rim package.
    kind === 'rim' ? '' : [boltPatternLabel(facts), etLabel(facts)].filter(Boolean).join(' '),
    kind === 'rim' ? etLabel(facts) : '',
    mounting && kind === 'wheel' ? 'monterat och balanserat' : '',
  ].filter(Boolean);

  const head = [lead, car].filter(Boolean).join(' ');
  return specs.length ? `${head} – ${specs.join(', ')}` : head;
};

/* Row labels, in the order a reader (and a crawler) should meet them: what size
 * it is, then how it attaches, then what condition it is in. The same order is
 * used for the visible table and for the JSON-LD additionalProperty list, so
 * the two read as the same answer. */
const FIT_FACT_ORDER = [
  'combination',
  'diameter',
  'boltPattern',
  'hubHole',
  'etFront',
  'rimWidthFront',
  'tiretype',
  'tireModel',
  'threaddepth',
  'loadIndex',
  'speedIndex',
  'dot',
  'tpms',
  'condition',
  'carFitment',
];

const FIT_FACT_LABELS = {
  combination: 'Dimension',
  diameter: 'Fälgdiameter',
  boltPattern: 'Bultmönster',
  hubHole: 'Navhål (CB)',
  etFront: 'Offset (ET)',
  rimWidthFront: 'Fälgbredd',
  tiretype: 'Däcktyp',
  tireModel: 'Däckmodell',
  threaddepth: 'Mönsterdjup',
  loadIndex: 'Lastindex',
  speedIndex: 'Hastighetsindex',
  dot: 'DOT',
  tpms: 'TPMS-sensorer',
  condition: 'Skick',
  carFitment: 'Passar',
};

/** Strip a unit the seller already typed so we never print "7mm mm". */
const mm = value => (has(value) ? `${clean(value).replace(/\s*mm\s*$/i, '')} mm` : '');

/**
 * The fit facts of one listing, as ordered rows (BR-3).
 *
 * Rows with no value are dropped rather than shown empty: a rim with no hub
 * bore on file — which is most of them — should say nothing about its hub bore,
 * not show a blank next to the label.
 *
 * @param {Object} facts listing publicData
 * @param {Object} [options]
 * @param {Function} [options.labelFor] (key, value) => display label, for the
 *   enum fields whose human labels live in the marketplace config. Defaults to
 *   the raw stored value.
 * @returns {Array<{key: String, label: String, value: String}>}
 */
const fitFactRows = (facts = {}, { labelFor } = {}) => {
  const label = typeof labelFor === 'function' ? labelFor : (key, value) => value;

  /* `hubHole` and `rimWidthFront` are enums whose option VALUES spell a decimal
   * with an underscore - 66.5 mm is stored as "66_5", 8.5" as "8_5". Correct in
   * the field, wrong on a page: this table was printing "Navhal (CB) 66_5 mm".
   * The other enums here already resolve through the Console labels, which
   * spell the readable form ("66_5" -> "66.5"); these two were reading the raw
   * value instead. When no resolver is supplied - server-side callers, SSR -
   * read the underscore as the decimal point it stands for rather than
   * printing it. */
  const decimalEnum = (key, value) => {
    const resolved = label(key, value);
    return resolved === value ? value.replace('_', '.') : resolved;
  };

  const values = {
    combination: tireSizePhrase(facts),
    diameter: has(facts.diameter) ? `${clean(facts.diameter)}"` : '',
    boltPattern: boltPatternLabel(facts),
    hubHole: has(facts.hubHole) ? `${decimalEnum('hubHole', clean(facts.hubHole))} mm` : '',
    etFront: etLabel(facts) ? clean(facts.etFront) : '',
    rimWidthFront: has(facts.rimWidthFront)
      ? `${decimalEnum('rimWidthFront', clean(facts.rimWidthFront))}"`
      : '',
    tiretype: label('tiretype', clean(facts.tiretype)),
    tireModel: clean(facts.tireModel) || clean(facts.rimModel),
    threaddepth: mm(facts.threaddepth),
    loadIndex: clean(facts.loadIndex),
    speedIndex: clean(facts.speedIndex),
    dot: clean(facts.dot),
    /* Only stated when the seller answered. `tpms` is a boolean, so an absent
     * answer and "no sensors" are the same value in JavaScript — and telling a
     * buyer there are no sensors when nobody was asked is a wrong claim. */
    tpms: facts.tpms === true ? 'Ja' : facts.tpms === false ? 'Nej' : '',
    condition: label('condition', clean(facts.condition)),
    carFitment: carLabel(facts),
  };

  return FIT_FACT_ORDER.filter(key => has(values[key])).map(key => ({
    key,
    label: FIT_FACT_LABELS[key],
    value: clean(values[key]),
  }));
};

/**
 * The formula title for a SINGLE listing rather than a package.
 *
 * Shape: <what it is> <car> – <size>, <bolt pattern> <ET>
 *   "Sommardäck – 235/45R18"
 *   "Fälg – 20\", 5x108 ET40"
 *   "Komplett vinterhjul Volvo XC60 – 235/55R19 dubbat, 5x108 ET45"
 *
 * Same machinery as the package title, two differences. The nouns are the
 * single-item ones, and it never says "monterat och balanserat": that is a
 * claim about a service, true by definition of a hjulpaket but not of every
 * complete wheel an importer happens to create — and this module does not
 * make claims a listing cannot back.
 *
 * Used by the import path as a fallback when a row carries no usable title,
 * which is how listings ended up called "Namnlös annons". It does NOT
 * replace a title a seller wrote: their words are data, not a placeholder.
 *
 * @param {Object} facts listing publicData
 * @returns {String} the title, or '' when there is not enough to build one
 */
const listingFormulaTitle = (facts = {}) =>
  packageFormulaTitle(facts, { nouns: LISTING_NOUN, mounting: false });

/**
 * The listing facts as a plain paragraph, for the stored description.
 *
 * Built from fitFactRows so it can never state something the fit table does
 * not, and vice versa. It deliberately does NOT reuse listingAnswerSentence:
 * the listing page already renders that sentence live from publicData, so
 * storing it too would print the same words twice on the same page.
 *
 * Only used when a row has no description at all. An empty description is
 * less harmful than it looks — the listing page composes its answer from
 * publicData and the Google feed generates its own spec sentence — but a
 * listing whose stored text is blank carries nothing into an export, a
 * catalogue row or anything else that reads the listing rather than the page.
 *
 * @param {Object} facts listing publicData
 * @param {Object} [options] passed to fitFactRows (labelFor)
 * @returns {String} one paragraph, or '' when the listing states no facts
 */
const listingSpecParagraph = (facts = {}, options = {}) =>
  fitFactRows(facts, options)
    .map(row => `${row.label} ${row.value}.`)
    .join(' ');

/**
 * The quotable first paragraph (BR-2).
 *
 * Built from what the caller can actually vouch for. Everything is optional:
 * the sentence gets shorter, never less true. `montering` is only mentioned
 * when the seller really offers it, and delivery only when the listing carries
 * a delivery time — those are the two claims a buyer would act on.
 *
 * @param {Object} options
 * @param {Object} options.facts listing publicData
 * @param {String} [options.priceLabel] formatted price, e.g. "12 900 kr"
 * @param {String} [options.priceCovers] what that price buys, e.g. "hela satsen"
 * @param {String} [options.city] where the wheels are
 * @param {String} [options.sellerName] who is selling
 * @param {Boolean} [options.monteringOffered] the seller offers montering
 * @param {Number} [options.deliveryDays] stated delivery time in days
 * @param {String} [options.availability] 'in_stock' | 'sold_out' | undefined
 * @returns {String} one to three sentences, or '' when there is nothing to say
 */
const listingAnswerSentence = ({
  facts = {},
  priceLabel = '',
  priceCovers = '',
  city = '',
  sellerName = '',
  monteringOffered = false,
  deliveryDays = null,
  availability = '',
} = {}) => {
  const kind = kindOf(facts);
  const isPackage = has(facts.packageType);
  const season = seasonOf(facts.tiretype);
  const car = carLabel(facts);
  const years = yearPhrase(facts);
  const size = tireSizePhrase(facts);
  const bolts = boltPatternLabel(facts);
  const et = etLabel(facts);

  // What the thing is, in words a person would use.
  const noun = isPackage
    ? `${kind === 'rim' ? '' : (season && season.adjective) || ''}${PACKAGE_NOUN[kind] || 'paket'}`
    : kind === 'tire'
    ? (season && season.noun) || 'däck'
    : kind === 'rim'
    ? 'fälgar'
    : kind === 'wheel'
    ? `kompletta ${(season && season.adjective) || ''}hjul`.replace('  ', ' ')
    : '';
  if (!noun) return '';

  const lead = isPackage && kind === 'wheel' ? `Komplett ${noun}` : noun;
  const opening = car
    ? `${lead.charAt(0).toUpperCase() + lead.slice(1)} som passar ${car}${years ? ` ${years}` : ''}`
    : lead.charAt(0).toUpperCase() + lead.slice(1);

  const specs = [
    size,
    kind === 'rim' && has(facts.diameter) ? `${clean(facts.diameter)}" fälg` : '',
    bolts ? `bultmönster ${bolts}` : '',
    et,
  ].filter(Boolean);

  const sentences = [specs.length ? `${opening}: ${specs.join(', ')}.` : `${opening}.`];

  /* A wheel package arrives ready to bolt on — that is the product, not an
   * add-on. Montering as a SERVICE is a separate claim and only made when the
   * seller has one active. */
  if (isPackage && kind === 'wheel') {
    sentences.push('Däcken är monterade och balanserade på fälgarna.');
  }
  /* On new complete wheels and packages montering is not an add-on: checkout
   * adds it and locks it (P25009-484 BR-5). */
  if (monteringOffered) {
    const target = listingTarget(facts);
    sentences.push(
      target && target.kind === 'wheel' && target.condition === 'new'
        ? 'Montering ingår och läggs till i kassan.'
        : 'Montering kan läggas till i kassan.'
    );
  }

  const where = [sellerName, city].filter(Boolean).join(' i ');
  const delivery =
    deliveryDays !== null && deliveryDays !== undefined && Number(deliveryDays) > 0
      ? `leverans inom ${Number(deliveryDays)} ${Number(deliveryDays) === 1 ? 'dag' : 'dagar'}`
      : '';
  if (where || delivery) {
    sentences.push(`${[where ? `Säljs av ${where}` : '', delivery].filter(Boolean).join(', ')}.`);
  }

  if (priceLabel) {
    sentences.push(`Pris ${priceLabel}${priceCovers ? ` för ${priceCovers}` : ''}.`);
  }
  if (availability === 'sold_out') {
    sentences.push('Den här annonsen är slutsåld.');
  }

  return sentences.join(' ');
};

module.exports = {
  FIT_FACT_LABELS,
  FIT_FACT_ORDER,
  LISTING_NOUN,
  PACKAGE_NOUN,
  boltPatternLabel,
  carLabel,
  etLabel,
  fitFactRows,
  kindOf,
  listingAnswerSentence,
  listingFormulaTitle,
  listingSpecParagraph,
  packageFormulaTitle,
  seasonOf,
  tireSize,
  tireSizePhrase,
  yearPhrase,
};
